<template>
  <v-container fluid grid-list-xl>
    <div>
      <v-layout row wrap justify-end pr-1 class="mb-2">
        <v-btn color="primary" class="mb-2" @click="searchModal = true">
          <v-icon left>fa-search</v-icon>
          Search
        </v-btn>
      </v-layout>

      <v-data-table
        :headers="columnsDisplayed"
        :items="rows"
        :pagination.sync="pagination"
        :loading="loading"
        :total-items="totalRows"
        :rows-per-page-items="pageCountOptions"
        item-key="id"
        class="elevation-1"
        must-sort
        no-data-text="No data found"
        no-results-text="No results found"
        @update:pagination="search"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="(header) in props.headers"
              :key="header.value"
              v-show="header.visible"
              :class="['font-weight-bold text-xs-left column sortable pa-1 ma-0', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value)"
            >
              {{ header.text }}
              <v-icon small>arrow_upward</v-icon>
            </th>
            <th class="ma-0 pa-0 text-xs-right">
              <v-btn icon flat color="primary" @click="reset">
                <v-icon>fa-refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <tr>
            <td
              v-for="(header) in columnsDisplayed"
              :key="header.value"
              v-show="header.visible"
              class="pa-1 ma-0"
              :class="(header.type === 'boolean') ? 'text-xs-center' : ''"
            >
              <template v-if="header.value === 'id'">
                <a class="primary--text font-weight-bold text-no-wrap" title="View details" @click="openDetail(props.item.id)">
                  <v-icon small color="primary" class="mr-1">fa-search-plus</v-icon>{{ props.item.id }}
                </a>
              </template>

              <template v-else-if="header.type === 'boolean'">
                <span v-if="props.item[header.value]">Yes</span>
                <div v-else class="grey--text">No</div>
              </template>

              <template v-else-if="header.type === 'datetime'">
                {{ datetimeFromUtc(props.item[header.value]) }}
              </template>

              <template v-else-if="header.clean">
                {{ cleanValue(props.item[header.value]) }}
              </template>

              <template v-else>
                {{ props.item[header.value] }}
              </template>
            </td>
            <td class="ma-0 pa-0 text-xs-right"></td>
          </tr>
        </template>

        <template slot="no-data">
          No results found
        </template>
      </v-data-table>
    </div>

    <!-- Search -->
    <v-dialog v-model="searchModal" max-width="800px" @submit.prevent @keydown.esc.prevent="searchModal=false">
      <v-toolbar dense>
        <v-toolbar-title>Search</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click.native="searchModal = false">
          <v-icon>fa-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text>
          <v-form @keyup.native.enter="searchNew">
            <v-container fluid>
              <v-alert :value="slowSearchWarning" color="primary" outline class="mb-3">
                Searching emails created more than two days ago may take a long time to load.
              </v-alert>

              <v-layout row wrap>
                <v-flex xs12 sm4>
                  <v-text-field label="From" hint="Contains" persistent-hint v-model="filter.from" box />
                </v-flex>
                <v-flex xs12 sm4>
                  <v-text-field label="To" hint="Contains" persistent-hint v-model="filter.to" box />
                </v-flex>
                <v-flex xs12 sm4>
                  <v-text-field label="Subject" hint="Contains" persistent-hint v-model="filter.subject" box />
                </v-flex>

                <v-flex xs12 sm6>
                  <v-text-field label="Created From" type="datetime-local" v-model="filter.created_start_date" box hide-details />
                </v-flex>
                <v-flex xs12 sm6>
                  <v-text-field label="Created To" type="datetime-local" v-model="filter.created_end_date" box hide-details />
                </v-flex>

                <v-flex xs6 sm3>
                  <v-select label="Processed" v-model="filter.is_processed" :items="booleanSelect" box hide-details />
                </v-flex>
                <v-flex xs6 sm3>
                  <v-select label="Error" v-model="filter.is_error" :items="booleanSelect" box hide-details />
                </v-flex>
              </v-layout>

              <v-divider class="primary" />

              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="searchNew">Search</v-btn>
                <v-btn @click="reset">Clear</v-btn>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Detail -->
    <v-dialog v-model="detailModal" scrollable max-width="900px" @keydown.esc.prevent="detailModal=false">
      <v-card>
        <v-toolbar dense card color="primary" dark>
          <v-toolbar-title>Email Detail{{ detail ? ' #' + detail.id : '' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon flat @click.native="detailModal = false">
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <div v-if="detailLoading" class="text-xs-center pa-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <div v-else-if="detail">
            <!-- Overview -->
            <v-layout row wrap>
              <v-flex xs12 sm6 md4><strong>ID:</strong> {{ detail.id }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Created:</strong> {{ datetimeFromUtc(detail.created) }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Message ID:</strong> {{ detail.message_id || '—' }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Account:</strong> {{ detail.account_id }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Terminal:</strong> {{ detail.terminal_id }}</v-flex>
              <v-flex xs12 sm6 md4><strong>User:</strong> {{ detail.user_id }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Processed:</strong> {{ detail.is_processed ? 'Yes' : 'No' }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Error:</strong> {{ detail.is_error ? 'Yes' : 'No' }}</v-flex>
              <v-flex xs12 sm6 md4><strong>Attachments:</strong> {{ detail.has_attachments ? 'Yes' : 'No' }}</v-flex>
            </v-layout>

            <v-divider class="my-3" />

            <!-- Request (the email that was sent) -->
            <h3 class="subheading font-weight-bold mb-2">Request</h3>
            <v-layout row wrap>
              <v-flex xs12 sm6><strong>From:</strong> {{ cleanValue(detail.from) }}</v-flex>
              <v-flex xs12 sm6><strong>To:</strong> {{ cleanValue(detail.to) }}</v-flex>
              <v-flex xs12><strong>Subject:</strong> {{ cleanValue(detail.subject) }}</v-flex>
            </v-layout>

            <!-- Body (HTML) and Response as collapsible panels (default closed) -->
            <v-expansion-panel expand class="mt-3">
              <v-expansion-panel-content>
                <template v-slot:header>
                  <div class="subheading font-weight-bold">Body (HTML)</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ decodeBase64(detail.bodyHtml) || '—' }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content>
                <template v-slot:header>
                  <div class="subheading font-weight-bold">Response</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ formatRaw(detail.raw_response) || '—' }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content v-if="formatRaw(detail.raw_error)">
                <template v-slot:header>
                  <div class="subheading font-weight-bold error--text">Error Response</div>
                </template>
                <v-card>
                  <v-card-text>
                    <pre class="detail-pre">{{ formatRaw(detail.raw_error) }}</pre>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Moment from 'moment'

import Bus from '@/bus'
import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'

export default {
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin],

  data: function () {
    return {
      booleanSelect: [
        {value: '*', text: 'All'},
        {value: '0', text: 'No'},
        {value: '1', text: 'Yes'}
      ],

      columns: [
        { value: 'id', text: 'ID', sortable: true, visible: true },
        { value: 'account_id', text: 'Account', sortable: true, visible: true },
        { value: 'terminal_id', text: 'Terminal', sortable: true, visible: true },
        { value: 'user_id', text: 'User', sortable: true, visible: true },
        { value: 'from', text: 'From', sortable: true, visible: true, clean: true },
        { value: 'to', text: 'To', sortable: true, visible: true, clean: true },
        { value: 'subject', text: 'Subject', sortable: true, visible: true, clean: true },
        { value: 'is_processed', text: 'Processed', sortable: true, visible: true, type: 'boolean' },
        { value: 'is_error', text: 'Error', sortable: true, visible: true, type: 'boolean' },
        { value: 'has_attachments', text: 'Attach', sortable: true, visible: true, type: 'boolean' },
        { value: 'created', text: 'Created', sortable: true, visible: true, type: 'datetime' }
      ],
      pagination: this.defaultPagination(),
      rows: [],
      totalRows: 0,
      pageCountOptions: [5, 10, 25, 50, 100],
      loading: true,

      searchModal: false,

      detailModal: false,
      detailLoading: false,
      detail: null,

      filter: this.defaultFilter()
    }
  },

  computed: {
    columnsDisplayed: function () {
      return this.columns.filter(column => column.visible)
    },

    // Warn before submitting: scanning back more than two days can be slow.
    slowSearchWarning: function () {
      return !!this.filter.created_start_date &&
        Moment(this.filter.created_start_date).isBefore(Moment().subtract(2, 'days'))
    }
  },

  methods: {
    defaultFilter () {
      return {
        from: '',
        to: '',
        subject: '',
        is_processed: '*',
        is_error: '*',
        // Default to the past day.
        created_start_date: Moment().subtract(1, 'day').format('YYYY-MM-DDTHH:mm'),
        created_end_date: ''
      }
    },

    defaultPagination () {
      return {
        sortBy: 'created',
        descending: true,
        rowsPerPage: this.$store.state.paginationDefaults.rowsPerPage,
        page: 1
      }
    },

    datetimeFromUtc (date) {
      if (date === null || !date) {
        return ''
      }
      return Moment.utc(date).local().format('MMM-DD-YYYY hh:mm:ssA')
    },

    // from/to/subject are stored JSON-stringified; strip the wrapping so the
    // display reads as a plain address/subject rather than "\"x@y.com\"".
    cleanValue (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      if (typeof value !== 'string') {
        return String(value)
      }
      try {
        let parsed = JSON.parse(value)
        if (typeof parsed === 'string') {
          return parsed
        }
        if (Array.isArray(parsed)) {
          return parsed.join(', ')
        }
        return JSON.stringify(parsed)
      } catch (e) {
        return value
      }
    },

    // body/bodyHtml are stored base64-encoded.
    decodeBase64 (value) {
      if (!value) {
        return ''
      }
      try {
        return decodeURIComponent(escape(window.atob(value)))
      } catch (e) {
        try {
          return window.atob(value)
        } catch (e2) {
          return value
        }
      }
    },

    // raw_response/raw_error may come back as an object (JSON column) or a
    // string (e.g. provider XML). Render either readably.
    formatRaw (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      if (typeof value === 'object') {
        return Object.keys(value).length ? JSON.stringify(value, null, 2) : ''
      }
      if (typeof value === 'string') {
        let trimmed = value.trim()
        if (trimmed === '' || trimmed === '{}' || trimmed === 'null') {
          return ''
        }
        try {
          return JSON.stringify(JSON.parse(value), null, 2)
        } catch (e) {
          return value
        }
      }
      return String(value)
    },

    searchNew () {
      this.pagination.page = 1
      this.search()
    },

    async search () {
      this.loading = true

      try {
        let filter = { ...this.filter }

        if (filter.created_start_date) {
          filter.created_start_date = Moment(filter.created_start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
        if (filter.created_end_date) {
          filter.created_end_date = Moment(filter.created_end_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }

        let result = await this.api({
          method: 'get',
          url: '/email_outgoing',
          params: {
            filter: filter,
            page: this.pagination.page - 1,
            perPage: this.pagination.rowsPerPage,
            orderBy: this.pagination.sortBy,
            orderDirection: this.pagination.descending ? 'desc' : 'asc'
          }
        })
        this.rows = result.data.rows
        this.totalRows = result.data.count
        this.searchModal = false
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.loading = false
      }
    },

    async openDetail (id) {
      this.detail = null
      this.detailLoading = true
      this.detailModal = true

      try {
        let result = await this.api({
          method: 'get',
          url: `/email_outgoing/${id}`
        })
        this.detail = result.data.row
      } catch (err) {
        this.detailModal = false
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.detailLoading = false
      }
    },

    reset () {
      this.filter = this.defaultFilter()
      this.pagination = this.defaultPagination()

      this.search()
    },

    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
      this.search()
    }
  }
}
</script>

<style scoped>
.detail-pre {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  max-height: 300px;
  background: rgba(0, 0, 0, 0.04);
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
}
a {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/manage/EmailOutgoing.vue